import { TableCell, TableRow } from '@material-ui/core'
import {
  useRef,
  useCallback,
  useMemo,
  useState,
  ChangeEvent,
  EventHandler,
  KeyboardEvent,
  FormEvent,
  MouseEvent
} from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

import { AddToReassignmentQueueModal } from '../reassignDonation/addToReassignmentQueueModal/AddToReassignmentQueueModal'
import { ReassignDonationWithMarkedModal } from '../reassignDonation/reassignDonationWithMarkedModal/ReassignDonationWithMarkedModal'

import { Alert, Badge, Box, SearchInput, useToast } from '@percent/lemonade'
import { ErrorInfo, ErrorView, FoundationNameWithFlag, Table } from '@percent/admin-dashboard/common/components'
import {
  useCurrencies,
  useFoundations,
  useMutation,
  useFilterValue,
  useCSVDownloader,
  usePermissions
} from '@percent/admin-dashboard/common/hooks'
import { dayJS } from '@percent/admin-dashboard/common/library/utility/date'
import { formatAmountFromMinorUnits } from '@percent/admin-dashboard/common/utility/formatAmount/formatAmount'
import { DonationsStatusFilter, DonationsTableProps, UploadError } from './DonationsTable.types'
import {
  DonationEnhanced,
  DonationReassignmentsBatchRow,
  DonationStatus,
  DonationsBatchRow,
  PayrollDeductionReportsImportRow,
  PostPayrollDeductionReportsImportResponse,
  ReassignmentKind,
  ReassignmentStatus
} from '@percent/admin-dashboard/api/actions/donations/donations.types'
import styles from './DonationsTable.module.scss'
import { DonationsActionsMenu } from './donationsActionsMenu/DonationsActionsMenu'
import { JsonViewDrawer } from '@percent/admin-dashboard/common/components/drawer/JsonViewDrawer'
import { DonationsFiltersAndActions } from './donationsFiltersAndActions/DonationsFiltersAndActions'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { DonationOnHoldFlag } from './donationOnHoldFlag/DonationOnHoldFlag'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { RemoveFromReassignmentQueueModal } from '@percent/admin-dashboard/app/donations/reassignDonation/removeFromReassignmentQueueModal/RemoveFromReassignmentQueueModal'
import { DownloadDonationsExport } from './donationsFiltersAndActions/DownloadDonationsExport/DownloadDonationsExport'
import { UploadDonationsCsv as UploadDonationsImport } from './donationsFiltersAndActions/UploadDonationsImport/UploadDonationsImport'
import { PendingPayrollDeductionsModal } from './donationsFiltersAndActions/PendingPayrollDeductionsModal/PendingPayrollDeductionsModal'
import { EditDonation } from './editDonation/EditDonation'

const cells = [
  { id: 'donation', isSortable: false, props: { width: '8%' } },
  { id: 'currency', isSortable: false, props: { width: '6.5%' } },
  { id: 'organization', isSortable: false, props: { width: '12.5%' } },
  { id: 'donor', isSortable: false, props: { width: '8%' } },
  { id: 'partner', isSortable: false, props: { width: '19.5%' } },
  { id: 'foundation', isSortable: false, props: { width: '17.5%' } },
  { id: 'status', isSortable: false, props: { width: '13.5%' } },
  { id: 'type', isSortable: false, props: { width: '5%' } },
  { id: 'donationCreated', isSortable: false, props: { width: '13%' } }
]

export function DonationsTable({
  isLoading,
  totalResults,
  data,
  previousPage,
  nextPage,
  errorMessage,
  queryParams,
  setQueryParams,
  refreshToFirstPage
}: DonationsTableProps) {
  const { t } = useTranslation()
  const { foundations } = useFoundations()
  const { currencyInfo } = useCurrencies()
  const exportCsvAnchorEl = useRef<HTMLElement | null>(null)
  const importCsvAnchorEl = useRef<HTMLElement | null>(null)
  const [isExportCsvOpened, setIsExportCsvOpened] = useState(false)
  const [isImportCsvOpened, setIsImportCsvOpened] = useState(false)
  const [selectedDonationDetails, setSelectedDonationDetails] = useState<DonationEnhanced | undefined>(undefined)
  const [isRawViewOpen, setIsRawViewOpen] = useState(false)
  const [isAddToReassignmentQueueModalOpen, setIsAddToReassignmentQueueModalOpen] = useState(false)
  const [isReassignMarkedDonationModalOpen, setIsReassignMarkedDonationModalOpen] = useState(false)
  const [isRemoveFromReassignmentQueueModalOpen, setIsRemoveFromReassignmentQueueModalOpen] = useState(false)
  const [isPendingPayrollDeductionsModalOpen, setIsPendingPayrollDeductionsModalOpen] = useState(false)
  const [isEditDonationModalOpen, setIsEditDonationModalOpen] = useState(false)

  const { donationsService } = useServices()
  const { addToast } = useToast()
  const [searchValue, setSearchValue] = useFilterValue<string>(queryParams.query || '', [queryParams.query])
  const [uploadError, setUploadError] = useState<UploadError | undefined>()
  const [payrollDeductionImportErrors, setPayrollDeductionImportErrors] = useState<
    PostPayrollDeductionReportsImportResponse['errors'] | undefined
  >(undefined)
  const [payrollDeductionImportSummary, setPayrollDeductionImportSummary] = useState<
    PostPayrollDeductionReportsImportResponse['summary'] | undefined
  >(undefined)
  const [payrollDeductionImportResults, setPayrollDeductionImportResults] = useState<
    PostPayrollDeductionReportsImportResponse['results'] | undefined
  >(undefined)
  const { userHasPermissions } = usePermissions()
  const reassignDonationFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_REASSIGN_DONATION)
  const internalFgaDoraDonationsFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_DORA_DONATIONS)

  const canReassign =
    reassignDonationFeatureFlag &&
    (!internalFgaDoraDonationsFeatureFlag || userHasPermissions('donations:can_reassign'))

  const canEditDonation = !internalFgaDoraDonationsFeatureFlag || userHasPermissions('donation:can_edit')

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleKeyChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }

  const handleKeyPress: EventHandler<KeyboardEvent<HTMLInputElement>> = useCallback(
    event => {
      if (event.key === 'Enter') {
        setQueryParams({
          ...queryParams,
          query: (event.target as HTMLInputElement).value
        })
      }

      if (event.key === 'Escape') {
        setSearchValue('')
      }

      return null
    },
    [queryParams, setQueryParams, setSearchValue]
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleClearValue = () => {
    setSearchValue('')
    setQueryParams({
      ...queryParams,
      query: undefined
    })
  }

  const tabs = useMemo(
    () => [
      t('status.all'),
      t('status.pending'),
      t('status.awaitingPayment'),
      t('status.receivedPayment'),
      t('status.settled'),
      t('status.disbursed'),
      t('status.cancelled')
    ],
    [t]
  )

  const donationsStatusValue = DonationsStatusFilter[queryParams.status as keyof typeof DonationsStatusFilter]

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    if (newValue < 1) {
      setQueryParams({ ...queryParams, status: undefined })
    } else {
      setQueryParams({ ...queryParams, status: DonationsStatusFilter[newValue] })
    }
  }

  const getBadgeVariant = (status: DonationStatus) => {
    switch (status) {
      case DonationStatus.CANCELLED:
        return (
          <Badge variant="critical" icon="reject">
            {t('status.cancelled')}
          </Badge>
        )
      case DonationStatus.DISBURSED:
        return (
          <Badge variant="positive" icon="check-badge">
            {t('status.disbursed')}
          </Badge>
        )
      case DonationStatus.RECEIVED_PAYMENT:
        return (
          <Badge variant="positive" icon="check-badge">
            {t('status.receivedPayment')}
          </Badge>
        )
      case DonationStatus.REQUESTED_PAYMENT:
        return (
          <Badge variant="informative" icon="info">
            {t('status.awaitingPayment')}
          </Badge>
        )
      case DonationStatus.PENDING:
        return (
          <Badge variant="default" icon="clock">
            {t('status.pending')}
          </Badge>
        )
      case DonationStatus.SETTLED:
        return (
          <Badge variant="positive" icon="check-badge">
            {t('status.settled')}
          </Badge>
        )
      default:
        return null
    }
  }

  const handleOnHoldOnlyChange = useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      setQueryParams({ ...queryParams, onHoldOnly: (event.target as HTMLInputElement).checked })
    },
    [queryParams, setQueryParams]
  )

  const handleExportCsvClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      exportCsvAnchorEl.current = event.currentTarget
      setIsExportCsvOpened(!isExportCsvOpened)
      setIsImportCsvOpened(false)
    },
    [isExportCsvOpened]
  )

  const handleImportCsvClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      importCsvAnchorEl.current = event.currentTarget
      setIsImportCsvOpened(!isImportCsvOpened)
      setIsExportCsvOpened(false)
    },
    [isImportCsvOpened]
  )

  const [{ isLoading: isDonationUploadInProgress }, { apiFunc: uploadDonationsBatch }] = useMutation(
    donationsService.uploadDonationsBatch,
    response => {
      addToast(t('toast.importDonationsSuccess', { numberOfDonations: response.data.data.donationsCount }), 'success')
      setQueryParams({
        status: undefined,
        partnerId: undefined,
        startDate: undefined,
        endDate: undefined,
        query: undefined,
        onHoldOnly: undefined
      })
      refreshToFirstPage()
      setUploadError(undefined)
    },
    (e: any) => {
      if (e.response?.data?.error) {
        setUploadError(e.response?.data?.error)
      } else {
        addToast(t('toast.importDonationsError'), 'error')
      }
    }
  )

  const [{ isLoading: isReassignmentUploadInProgress }, { apiFunc: uploadDonationReassignmentsBatch }] = useMutation(
    donationsService.uploadDonationReassignmentsBatch,
    response => {
      addToast(
        t('toast.importDonationReassignmentsSuccess', { numberOfReassignments: response.data.data.reassignedCount }),
        'success'
      )
      setQueryParams({
        status: undefined,
        partnerId: undefined,
        startDate: undefined,
        endDate: undefined,
        query: undefined,
        onHoldOnly: undefined
      })
      refreshToFirstPage()
      setUploadError(undefined)
    },
    (e: any) => {
      if (e.response?.data?.error) {
        setUploadError(e.response?.data?.error)
      } else {
        addToast(t('toast.importDonationReassignmentsError'), 'error')
      }
    }
  )

  const [{ isLoading: isPayrollDeductionUploadInProgress }, { apiFunc: uploadPayrollDeductionReportsImport }] =
    useMutation(
      donationsService.uploadPayrollDeductionReportsImport,
      (response: { data: PostPayrollDeductionReportsImportResponse }) => {
        // Response structure: { message, processed, summary, results, errors }
        const responseData = response.data
        const { processed, summary, errors } = responseData
        const hasErrors = errors && errors.length > 0
        const totalProcessed =
          summary.approved +
          summary.rejected +
          summary.approved_partial +
          summary.approved_excess +
          summary.already_processed

        // Store errors and results for display
        if (hasErrors) {
          setPayrollDeductionImportErrors(errors)
          setPayrollDeductionImportSummary(undefined)
          setPayrollDeductionImportResults(undefined)
        } else {
          setPayrollDeductionImportErrors(undefined)
          setPayrollDeductionImportSummary(summary)
          setPayrollDeductionImportResults(responseData.results)
        }

        // Show toast with summary
        if (hasErrors) {
          addToast(
            t('toast.importAuthorisedPayrollDeductionsPartialSuccess', {
              processed: totalProcessed,
              errors: errors.length
            }),
            'error' as const
          )
        } else {
          addToast(
            t('toast.importAuthorisedPayrollDeductionsSuccess', {
              processed,
              approved: summary.approved,
              rejected: summary.rejected,
              approvedPartial: summary.approved_partial,
              approvedExcess: summary.approved_excess,
              alreadyProcessed: summary.already_processed
            }),
            'success' as const
          )
        }

        setQueryParams({
          status: undefined,
          partnerId: undefined,
          startDate: undefined,
          endDate: undefined,
          query: undefined,
          onHoldOnly: undefined
        })
        refreshToFirstPage()
        setUploadError(undefined)
      },
      (e: any) => {
        if (e.response?.data?.error) {
          setUploadError(e.response?.data?.error)
        } else {
          addToast(t('toast.importAuthorisedPayrollDeductionsError'), 'error')
        }
      }
    )

  const reassignmentKinds = [ReassignmentKind.Unpayable]
  const reassignmentStatuses = [ReassignmentStatus.PENDING]

  const [{ loading: isReassignmentsDownloadInProgress, errorCSVMessage }, { downloadCSV }] = useCSVDownloader({
    service: () =>
      donationsService.downloadReassignmentsCsv({
        reassignmentKinds,
        reassignmentStatuses
      }),
    fileName: `donation_reassignments_report_${reassignmentKinds.join('+')}_${reassignmentStatuses.join('+')}_${dayjs(
      new Date()
    ).format('YYYY-MM-DD')}`,
    successMessage: t('toast.exportDonationReassignmentsSuccess')
  })

  const handleDonationBatchCreation = useCallback(
    async (payload: DonationsBatchRow[]) => {
      await uploadDonationsBatch({ payload })
    },
    [uploadDonationsBatch]
  )

  const handleDonationReassignmentBatchCreation = useCallback(
    async (input: DonationReassignmentsBatchRow[]) => {
      const payload = [
        'Donation ID,Reassignment Reason,Old Organisation ID,New Organisation ID',
        ...input.map(
          ({ donationId, reassignmentReason, oldOrganisationId, newOrganisationId }) =>
            `${donationId},${reassignmentReason},${oldOrganisationId},${newOrganisationId}`
        )
      ].join('\n')
      await uploadDonationReassignmentsBatch({ payload })
    },
    [uploadDonationReassignmentsBatch]
  )

  const handlePayrollDeductionReportsImport = useCallback(
    async (input: PayrollDeductionReportsImportRow[], partnerId: string) => {
      const payload = [
        'Donation ID,Currency,Payroll Deduction Amount',
        ...input.map(
          ({ donationId, currencyCode, payrollDeductionAmount }) =>
            `${donationId},${currencyCode},${payrollDeductionAmount}`
        )
      ].join('\n')
      await uploadPayrollDeductionReportsImport({ payload, partnerId })
    },
    [uploadPayrollDeductionReportsImport]
  )

  const handleDownloadPendingReassignmentsCsv = async () => {
    if (downloadCSV) {
      await downloadCSV()
    }
  }

  const filtersAndActions = useMemo(
    () => (
      <Box className={styles.filtersAndActionsWrapper}>
        <div className={styles.searchWrapper}>
          <SearchInput
            value={searchValue ?? ''}
            handleClearValue={handleClearValue}
            onKeyDown={handleKeyPress}
            onChange={handleKeyChange}
            placeholder={t('table.adminDonationSearchPlaceholder')}
          />
        </div>
        <DonationsFiltersAndActions
          queryParams={queryParams}
          setQueryParams={setQueryParams}
          handleExportCsvClick={handleExportCsvClick}
          handleImportCsvClick={handleImportCsvClick}
          handleOnHoldOnlyChange={handleOnHoldOnlyChange}
          isUploadInProgress={
            !!(isDonationUploadInProgress ?? isReassignmentUploadInProgress ?? isPayrollDeductionUploadInProgress)
          }
        />
        {uploadError ? (
          <ErrorInfo
            header={`
            ${t('typography.donationsImportErrorTitle')} ${uploadError.message}
            `}
            reasons={uploadError.reasons}
            footer={t('typography.donationsImportErrorInstruction')}
          />
        ) : null}
        {payrollDeductionImportErrors && payrollDeductionImportErrors.length > 0 ? (
          <ErrorInfo
            header={t('typography.payrollDeductionImportErrorsTitle', {
              count: payrollDeductionImportErrors.length
            })}
            reasons={payrollDeductionImportErrors.map(
              err => `Row ${err.row} - Donation ID: ${err.donationId} - ${err.error}: ${err.details}`
            )}
            footer={t('typography.payrollDeductionImportErrorsFooter')}
          />
        ) : null}
        {payrollDeductionImportSummary && payrollDeductionImportResults ? (
          <Box mb={16}>
            <Alert
              variant="success"
              title={t('typography.payrollDeductionImportSuccessTitle', {
                processed: payrollDeductionImportResults.length
              })}
              descriptionVariant="div"
            >
              <ul className={styles.importSummaryList}>
                {payrollDeductionImportSummary.approved > 0 && (
                  <li>
                    {t('typography.payrollDeductionImportApproved', {
                      count: payrollDeductionImportSummary.approved
                    })}
                  </li>
                )}
                {payrollDeductionImportSummary.rejected > 0 && (
                  <li>
                    {t('typography.payrollDeductionImportRejected', {
                      count: payrollDeductionImportSummary.rejected
                    })}
                  </li>
                )}
                {payrollDeductionImportSummary.approved_partial > 0 && (
                  <li>
                    {t('typography.payrollDeductionImportApprovedPartial', {
                      count: payrollDeductionImportSummary.approved_partial
                    })}
                  </li>
                )}
                {payrollDeductionImportSummary.approved_excess > 0 && (
                  <li>
                    {t('typography.payrollDeductionImportApprovedExcess', {
                      count: payrollDeductionImportSummary.approved_excess
                    })}
                  </li>
                )}
                {payrollDeductionImportSummary.already_processed > 0 && (
                  <li>
                    {t('typography.payrollDeductionImportAlreadyProcessed', {
                      count: payrollDeductionImportSummary.already_processed
                    })}
                  </li>
                )}
              </ul>
              {payrollDeductionImportResults.length > 0 && (
                <Box mt={8}>
                  <strong>{t('typography.payrollDeductionImportResultsDetails')}:</strong>
                  <ul className={styles.importResultsList}>
                    {payrollDeductionImportResults.map(result => (
                      <li key={result.donationId}>
                        {t(`typography.payrollDeductionImportAction.${result.action}`, {
                          donationId: result.donationId,
                          deductedAmount: result.deductedAmount,
                          originalAmount: result.originalAmount
                        })}
                      </li>
                    ))}
                  </ul>
                </Box>
              )}
            </Alert>
          </Box>
        ) : null}
      </Box>
    ),
    [
      searchValue,
      handleClearValue,
      handleKeyPress,
      handleKeyChange,
      t,
      queryParams,
      setQueryParams,
      handleExportCsvClick,
      handleImportCsvClick,
      handleOnHoldOnlyChange,
      isDonationUploadInProgress,
      isReassignmentUploadInProgress,
      isPayrollDeductionUploadInProgress,
      uploadError,
      payrollDeductionImportErrors,
      payrollDeductionImportSummary,
      payrollDeductionImportResults
    ]
  )

  if (errorCSVMessage) {
    return <ErrorView errorMessage={errorCSVMessage} />
  }

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <Box className={styles.tableWrapper}>
      <Table
        isLoading={isLoading}
        totalResults={totalResults}
        data={data}
        previousPage={previousPage}
        nextPage={nextPage}
        columns={cells}
        orderBy="donationCreated"
        emptyTableText={t('table.emptyDonations')}
        tabs={tabs}
        value={donationsStatusValue ?? 0}
        handleChange={handleTabChange}
        filtersContent={filtersAndActions}
      >
        {data?.map(row => {
          const foundation = row?.foundationId ? foundations?.[row?.foundationId] : undefined
          const isOnHold = reassignDonationFeatureFlag && !!row.pendingReassignment

          return (
            <TableRow key={row.id}>
              <TableCell>
                <div className={isOnHold ? styles.onHoldCell : ''}>
                  {formatAmountFromMinorUnits({
                    currencyCode: row.amount.currency,
                    value: row.amount.amount,
                    currencies: currencyInfo
                  })}
                </div>
              </TableCell>
              <TableCell>
                <div className={isOnHold ? styles.onHoldCell : ''}>{row.amount.currency}</div>
              </TableCell>
              <TableCell>
                <div className={isOnHold ? styles.onHoldCell : ''}>
                  {row.organisation?.displayName ?? row.organisation?.name ?? row.organisationId}
                </div>
              </TableCell>
              <TableCell>
                <div className={isOnHold ? styles.donorWrapperOnHold : styles.donorWrapper}>
                  <span>
                    {row.firstName} {row.lastName}
                  </span>
                  <a className={styles.email} href={`mailto:${row.email}`}>
                    {row.email}
                  </a>
                </div>
              </TableCell>
              <TableCell>
                <div className={isOnHold ? styles.onHoldCell : ''}>{row.partner?.name ?? row.partnerId}</div>
              </TableCell>
              <TableCell>
                <div className={isOnHold ? styles.onHoldCell : ''}>
                  {foundation ? (
                    <FoundationNameWithFlag countryCode={foundation.countryCode} name={foundation.name} />
                  ) : null}
                </div>
              </TableCell>
              <TableCell>
                <div className={styles.statusWrapper}>
                  {getBadgeVariant(row.status)}
                  {reassignDonationFeatureFlag && (
                    <DonationOnHoldFlag isOnHold={!!row.pendingReassignment} reason={row.pendingReassignment?.reason} />
                  )}
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="default">{t(`typography.donationType.${row.type}`)}</Badge>
              </TableCell>
              <TableCell>
                <div className={isOnHold ? styles.onHoldCell : ''}>
                  {dayJS(row.createdAt).format('DD MMM YYYY, HH:mm')}
                </div>
              </TableCell>
              <TableCell align="right" className={styles.menuCell}>
                <DonationsActionsMenu
                  handleViewRaw={() => setIsRawViewOpen(true)}
                  handleReassignMarkedDonation={() => {
                    setIsReassignMarkedDonationModalOpen(true)
                  }}
                  handleRemoveFromReassignmentQueue={() => {
                    setIsRemoveFromReassignmentQueueModalOpen(true)
                  }}
                  handleAddToReassignmentQueue={() => setIsAddToReassignmentQueueModalOpen(true)}
                  handleEditDonation={() => setIsEditDonationModalOpen(true)}
                  onSelectDonation={setSelectedDonationDetails}
                  donation={row}
                  isReassignDisabled={!canReassign}
                  triggerClassName={styles.menuBtn}
                  isEditDonationDisabled={!canEditDonation}
                />
              </TableCell>
            </TableRow>
          )
        })}
      </Table>
      {selectedDonationDetails ? (
        <>
          <JsonViewDrawer
            isOpen={isRawViewOpen}
            rawJsonData={selectedDonationDetails}
            onClose={() => {
              setIsRawViewOpen(false)
              setSelectedDonationDetails(undefined)
            }}
          />
          {canEditDonation && (
            <EditDonation
              donationId={selectedDonationDetails.id}
              externalPaymentUrl={selectedDonationDetails.externalPaymentUrl}
              open={isEditDonationModalOpen}
              onClose={() => {
                setIsEditDonationModalOpen(false)
                setSelectedDonationDetails(undefined)
              }}
              onSuccess={refreshToFirstPage}
            />
          )}
        </>
      ) : null}
      {canReassign && selectedDonationDetails && (
        <>
          <RemoveFromReassignmentQueueModal
            open={isRemoveFromReassignmentQueueModalOpen}
            onClose={() => {
              setIsRemoveFromReassignmentQueueModalOpen(false)
              setSelectedDonationDetails(undefined)
              refreshToFirstPage()
            }}
            selectedDonation={selectedDonationDetails}
          />
          <AddToReassignmentQueueModal
            open={isAddToReassignmentQueueModalOpen}
            refresh={refreshToFirstPage}
            sourceOrganisationName={
              selectedDonationDetails.organisation?.name ?? selectedDonationDetails.organisationId
            }
            selectedDonation={selectedDonationDetails}
            onClose={() => {
              setIsAddToReassignmentQueueModalOpen(false)
              setSelectedDonationDetails(undefined)
            }}
          />
          <ReassignDonationWithMarkedModal
            open={isReassignMarkedDonationModalOpen}
            refresh={refreshToFirstPage}
            sourceOrganisationName={
              selectedDonationDetails.organisation?.name ?? selectedDonationDetails.organisationId
            }
            selectedDonation={selectedDonationDetails}
            onClose={() => {
              setIsReassignMarkedDonationModalOpen(false)
              setSelectedDonationDetails(undefined)
            }}
          />
        </>
      )}
      {reassignDonationFeatureFlag && (
        <DownloadDonationsExport
          open={isExportCsvOpened}
          setOpen={setIsExportCsvOpened}
          anchorEl={exportCsvAnchorEl.current}
          handleDownloadReassignmentsCsv={handleDownloadPendingReassignmentsCsv}
          handleOpenPayrollDeductionsModal={() => setIsPendingPayrollDeductionsModalOpen(true)}
          isDownloadInProgress={!!isReassignmentsDownloadInProgress}
          canReassign={canReassign}
        />
      )}
      <PendingPayrollDeductionsModal
        open={isPendingPayrollDeductionsModalOpen}
        onClose={() => setIsPendingPayrollDeductionsModalOpen(false)}
      />
      <UploadDonationsImport
        open={isImportCsvOpened}
        setOpen={setIsImportCsvOpened}
        anchorEl={importCsvAnchorEl.current}
        handleDonationBatchCreation={handleDonationBatchCreation}
        handleDonationReassignmentBatchCreation={handleDonationReassignmentBatchCreation}
        handlePayrollDeductionReportsImport={handlePayrollDeductionReportsImport}
        isUploadInProgress={
          !!(isDonationUploadInProgress ?? isReassignmentUploadInProgress ?? isPayrollDeductionUploadInProgress)
        }
        partnerId={queryParams.partnerId ?? undefined}
        canReassign={canReassign}
      />
    </Box>
  )
}
